import { QueryKeys } from "@/constants/Query";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";

export interface CreateEpisodeOfCareParams {
  motherEncounterId: string;
  patientId: string;
  conditionCode?: fhirTypes.CodeableConcept;
  existingEpisodeOfCareId?: string;
}

interface CreateEpisodeOfCareResponse {
  childEncounter: fhirTypes.Encounter;
  motherEncounter: fhirTypes.Encounter;
  episodeOfCare: fhirTypes.EpisodeOfCare;
  condition: fhirTypes.Condition;
}

export const useCreateEpisodeOfCare = ({
  encounterId,
  onError,
}: {
  encounterId?: string;
  onError?: (error: ApiError) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { patientId } = useParams({ from: "/patient/$patientId" });

  return useApiMutation<CreateEpisodeOfCareResponse, CreateEpisodeOfCareParams>(
    {
      mutationOptions: {
        mutationKey: QueryKeys.Patient.Chart.EpisodesOfCare.CREATE({
          patientId,
        }),
        mutationFn: (params) => {
          return axiosInstance.post(
            "/charting/encounter-charting/episode-of-care/add",
            params,
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
              patientId,
              encounterId,
            }),
          });

          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(
                patientId,
              ),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Distribution.AVAILABLE_ACTIONS({
              patientId,
              encounterId,
            }),
          });
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
                patientId,
              }),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.EpisodesOfCare.LIST(patientId),
          });
        },
        onError: withMultipleEncountersHandler(onError),
      },
    },
  );
};
